
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ssoBaseUrl, clearTokens } from "../components/auth";
import "../App.css";

function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [providers, setProviders] = useState([]);
  const [selectedTenantKey, setSelectedTenantKey] = useState("");

  useEffect(() => {
    // Clear any residual session when landing on login page (e.g. after federated logout)
    clearTokens();
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("role");
    localStorage.removeItem("userRole");

    async function loadProviders() {
      try {
        const res = await fetch(`${ssoBaseUrl()}/api/v1/auth/providers`);
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setProviders(data);
            setSelectedTenantKey(data[0].tenantKey);
            return;
          }
        }
      } catch (err) {
        console.warn("Failed to load providers from SSO backend:", err);
      }

      const defaultTenant = process.env.REACT_APP_SSO_TENANT_KEY || "acme-kc";
      setProviders([{ tenantKey: defaultTenant, name: "Corporate SSO", providerType: "OIDC" }]);
      setSelectedTenantKey(defaultTenant);
    }

    loadProviders();
  }, []);

  const handlePasswordLogin = (event) => {
    event.preventDefault();
    setError("");

    const expectedUsername = process.env.REACT_APP_LOCAL_LOGIN_USERNAME || "superadmin";
    const expectedPassword = process.env.REACT_APP_LOCAL_LOGIN_PASSWORD || "SuperAdmin@123";

    if (username === expectedUsername && password === expectedPassword) {
      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("role", "SUPER_ADMIN");
      navigate("/home");
      return;
    }

    setError("Invalid username or password.");
  };

  const handleSsoLogin = () => {
    const tenantKey = selectedTenantKey || providers[0]?.tenantKey || "acme-kc";
    window.location.assign(`${ssoBaseUrl()}/api/v1/auth/login/${tenantKey}`);
  };

  const currentProvider = providers.find((p) => p.tenantKey === selectedTenantKey) || providers[0];

  return (
    <div className="login-page">
      <div className="login-card">

        {/* Logo */}
        <div className="login-logo">
          <img
            src="/cestasoft-logo.png"
            alt="CestaSoft Logo"
            className="login-logo-image"
          />
        </div>

        <p className="login-subtitle">
          Sign in to CestaConnect
        </p>

        <form onSubmit={handlePasswordLogin}>
          <div className="login-field">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              placeholder="Enter username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              required
            />
          </div>

          <div className="login-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          {error && <div className="login-error">{error}</div>}

          <button type="submit" className="login-button">
            Sign in with username
          </button>
        </form>

        <div className="login-divider">
          <span>OR</span>
        </div>

        {/* Multi-IdP Selector if more than 1 provider configured */}
        {providers.length > 1 && (
          <div className="login-field" style={{ marginBottom: "14px", textAlign: "left" }}>
            <label htmlFor="idp-select" style={{ fontSize: "12px", color: "#64748b", fontWeight: 600, display: "block", marginBottom: "6px" }}>
              Select Identity Provider
            </label>
            <select
              id="idp-select"
              value={selectedTenantKey}
              onChange={(e) => setSelectedTenantKey(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                fontSize: "14px",
                fontWeight: 500,
                color: "#1e293b",
                cursor: "pointer",
                outline: "none"
              }}
            >
              {providers.map((p) => (
                <option key={p.tenantKey} value={p.tenantKey}>
                  {p.name} {p.providerType ? `(${p.providerType})` : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* SSO Login Trigger */}
        <button
          type="button"
          className="login-button sso-button"
          onClick={handleSsoLogin}
        >
          {providers.length > 1
            ? `Sign in with ${currentProvider?.name || "Selected SSO"}`
            : (providers[0]?.name ? `Sign in with ${providers[0].name}` : "Sign in with SSO")}
        </button>

      </div>
    </div>
  );
}

export default Login;
